import React, { useCallback, useImperativeHandle, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import SydModal from '../../../../commonDesign/SydModal'
import SydButton from '../../../../commonDesign/Button'
import SydInput from '../../../../commonDesign/SydInput'
import SydTextArea from '../../../../commonDesign/SydTextArea'

const useStyles = makeStyles(() => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px'
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  label: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--font-color-main)'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '0 16px 16px'
  }
}))

const SaveQueryDialog = React.forwardRef(function SaveQueryDialog ({ onSave }, ref) {
  const classes = useStyles()
  const [dialogState, setDialogState] = useState({ open: false })
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const close = useCallback(() => {
    setDialogState({ open: false })
    setName('')
    setDescription('')
  }, [])

  useImperativeHandle(ref, () => ({
    open: (defaults = {}) => {
      setName(defaults.name || '')
      setDescription(defaults.description || '')
      setDialogState({
        open: true,
        mode: defaults.mode || 'save'
      })
    }
  }), [])

  const handleSave = useCallback(() => {
    if (!name.trim()) return
    onSave({
      name: name.trim(),
      description: description.trim() || null
    })
    close()
  }, [name, description, onSave, close])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && name.trim()) {
      handleSave()
    }
  }, [handleSave, name])

  const title = dialogState.mode === 'saveAs' ? 'Save Query As' : 'Save Query'

  return (
    <SydModal
      title={title}
      open={dialogState.open}
      onClose={close}
      size='small'
      position='top'
      disablePortal={false}
    >
      <div className={classes.form}>
        <div className={classes.field}>
          <label className={classes.label}>Name *</label>
          <SydInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='Enter a name for this query'
            autoFocus
            size='sm'
            minWidth='0'
            inputProps={{ maxLength: 256 }}
          />
        </div>
        <div className={classes.field}>
          <label className={classes.label}>Description</label>
          <SydTextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='Optional description'
            size='sm'
            minWidth='0'
            inputProps={{ rows: 3 }}
          />
        </div>
      </div>
      <div className={classes.actions}>
        <SydButton size='sm' variant='ghost' onClick={close}>
          Cancel
        </SydButton>
        <SydButton size='sm' variant='primary' onClick={handleSave} disabled={!name.trim()}>
          Save
        </SydButton>
      </div>
    </SydModal>
  )
})

SaveQueryDialog.propTypes = {
  onSave: PropTypes.func
}

SaveQueryDialog.defaultProps = {
  onSave: () => {}
}

export default SaveQueryDialog
